import React, { useEffect, useState, useMemo } from 'react';
import { collection, doc, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Wizard from './Wizard';
import { applyTheme, DEFAULT_THEME } from '../themes';
import './Home.css';
import '../ligeria.css';

const CATEGORY_ICONS = {
  'Sandwiches': '🥙',
  'Tacos': '🌯',
  'Burgers': '🍔',
  'Menu Enfant': '🍟',
  'Petit Faim': '🍗',
};

const imgUrl = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/b_white,f_jpg/');
};

const pad = (n) => String(n).padStart(2, '0');
const toMin = (str) => { const [h, m] = (str || '00:00').split(':').map(Number); return h * 60 + m; };
const toStr = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
const money = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`;

// ── Points de retrait ──
const POINTS = {
  lig: {
    nom: 'Le Ligeria',
    tag: 'Sur place',
    adresse: '1 Le Cul du Moulin, 49270 Orée d’Anjou',
    desc: () => 'Retrait au restaurant, dès que votre pizza est prête.',
    note: (s) => `Préparation ${Number(s.delai) || 15} min, retrait au restaurant.`,
  },
  tab: {
    nom: 'Tabac Oudon',
    tag: 'Point relais',
    adresse: '57 Rue Alphonse Fouschard, 44521 Oudon',
    desc: (s) => `Votre pizza arrive par navette, toutes les ${Number(s.navInt) || 30} minutes, jusqu'à ${(s.tabacDernier || '21:00').replace(':', 'h')}.`,
    note: (s) => `Préparation ${Number(s.delai) || 15} min, puis navette toutes les ${Number(s.navInt) || 30} min : seules les navettes que votre pizza peut encore attraper sont proposées.`,
  },
};

// SLOTS-START
// Grille ancrée sur l'ouverture de chaque service :
//   Ligeria     : ouverture + k × pas      (settings.step,   10 min par défaut)
//   Tabac Oudon : ouverture + k × navette  (settings.navInt, 30 min par défaut)
// Premier créneau = premier point de grille >= prêt (max(maintenant, ouverture) + préparation).
// Une pizza prête pile à l'heure de la navette la prend. L'ouverture n'est jamais proposée.
// Tabac Oudon : aucun retrait après settings.tabacDernier (21:00 par défaut) et pas de commande pour le lendemain.
function computeSlots(settings, pt, demain, now) {
  if (!pt) return [];
  if (pt === 'tab' && demain) return [];
  const limiteTabac = toMin(settings.tabacDernier || '21:00');
  const d = now || new Date();
  const nowMin = d.getHours() * 60 + d.getMinutes();
  const delai = Number(settings.delai) || 15;
  const pas = pt === 'tab' ? (Number(settings.navInt) || 30) : (Number(settings.step) || 10);
  const out = [];
  (settings.services || []).filter(s => s.actif !== false).forEach(s => {
    const ouv = toMin(s.ouverture);
    const fer = toMin(s.fermeture);
    const pret = (demain ? ouv : Math.max(nowMin, ouv)) + delai;
    for (let m = ouv; m <= fer; m += pas) if (m >= pret && (pt !== 'tab' || m <= limiteTabac)) out.push(toStr(m));
  });
  return [...new Set(out)].sort();
}
// SLOTS-END

function Home() {
  const [produits, setProduits] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [images, setImages] = useState({});
  const [settings, setSettings] = useState({ delai: 15, services: [{ ouverture: '11:00', fermeture: '22:00', actif: true }], ferme: false });
  const [panier, setPanier] = useState([]);
  const [wizardProduit, setWizardProduit] = useState(null);
  const [wizardInitialPicks, setWizardInitialPicks] = useState({});
  const [wizardInitialNote, setWizardInitialNote] = useState('');
  const [wizardInitialQty, setWizardInitialQty] = useState(1);
  const [editIndex, setEditIndex] = useState(null); // null = nouveau, number = modification
  const [wizardEditMode, setWizardEditMode] = useState(false);
  const [subView, setSubView] = useState(null); // { produit, items, groupId }
  const [showOrder, setShowOrder] = useState(false);
  const [orderDone, setOrderDone] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [liveOrder, setLiveOrder] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [sending, setSending] = useState(false);
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [heure, setHeure] = useState('');
  const [heureConfirmed, setHeureConfirmed] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [timePickerPage, setTimePickerPage] = useState(0);
  const [pendingWizard, setPendingWizard] = useState(null); // {produit, groupId, item}
  const [tick, setTick] = useState(0);
  const [point, setPoint] = useState(null);                 // 'lig' | 'tab'
  const [showPointPicker, setShowPointPicker] = useState(false);
  const [returnToCart, setReturnToCart] = useState(false);  // retour au panier après changement de lieu

  // Refresh available slots every 30 seconds so stale slots disappear
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const u0 = onSnapshot(doc(db, 'settings', 'general'), s => {
      if (s.exists()) {
        const data = s.data();
        setSettings(prev => ({ ...prev, ...data }));
        // 'green' était le thème par défaut de l'ancienne carte Biscornue :
        // on le considère comme « non choisi » et on applique le thème maison.
        const th = (!data.themeId || data.themeId === 'green') ? DEFAULT_THEME : data.themeId;
        applyTheme(th);
      }
    }, () => {});

    const u1 = onSnapshot(collection(db, 'produits'), snap => {
      const list = [];
      snap.forEach(d => { const data = d.data(); if (data.actif !== false && !data.deleted && !data.hidden) list.push({ id: d.id, ...data }); });
      list.sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999));
      setProduits(list);
    }, () => {});

    const u3 = onSnapshot(collection(db, 'optionGroups'), snap => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setOptionGroups(list);
    }, () => {});

    const u4 = onSnapshot(collection(db, 'images'), snap => {
      const imgs = {};
      snap.forEach(d => { if (d.data().image) imgs[d.id] = d.data().image; });
      setImages(imgs);
    }, () => {});

    return () => { u0(); u1(); u3(); u4(); };
  }, []);

  useEffect(() => {
    if (!orderId) return;
    const unsub = onSnapshot(doc(db, 'siparisler', orderId), snap => {
      if (snap.exists()) setLiveOrder(snap.data());
    });
    return unsub;
  }, [orderId]);

  useEffect(() => {
    if (liveOrder?.statut !== 'accepté' || !liveOrder?.heure) { setCountdown(null); return; }
    const calc = () => {
      const now = new Date();
      const [h, m] = liveOrder.heure.split(':').map(Number);
      const target = new Date(now); target.setHours(h, m, 0, 0);
      const diff = Math.max(0, Math.floor((target - now) / 1000));
      setCountdown(diff);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [liveOrder?.statut, liveOrder?.heure]);

  const creneaux = useMemo(() => computeSlots(settings, point, false), [settings, tick, point]); // eslint-disable-line react-hooks/exhaustive-deps
  const creneauxDemain = useMemo(() => computeSlots(settings, point, true), [settings, point]);

  const commandePourDemain = !settings.ferme && creneaux.length === 0 && creneauxDemain.length > 0;

  const creneauxActifs = commandePourDemain ? creneauxDemain : creneaux;

  const demainLabel = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }, []);

  useEffect(() => {
    // Ne reset que si aucune heure choisie ou si le créneau sélectionné n'existe plus
    if (!heure || !creneauxActifs.includes(heure)) {
      setHeure(creneauxActifs[0] || '');
    }
  }, [creneauxActifs]);

  const isOuvert = useMemo(() => {
    if (settings.ferme) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return (settings.services || []).some(s => s.actif !== false && nowMin >= toMin(s.ouverture) && nowMin <= toMin(s.fermeture));
  }, [settings]);

  // Catégories dans l'ordre d'apparition (les produits sont déjà triés par `ordre`)
  const categories = useMemo(() => {
    const out = [];
    produits.forEach(p => {
      const c = p.category || '';
      if (!out.includes(c)) out.push(c);
    });
    return out;
  }, [produits]);

  const ouvrirWizard = (produit) => {
    setWizardProduit(produit);
  };

  const openSubView = (produit) => {
    const firstGroupId = produit.optionGroupIds?.[0];
    if (!firstGroupId) { ouvrirWizard(produit); return; }
    const raw = optionGroups.find(g => g.id === firstGroupId);
    if (!raw) { ouvrirWizard(produit); return; }
    const items = (raw.options || raw.items || [])
      .map(it => ({ ...it, label: it.label || it.nom || '', price: Number(it.price ?? it.prix ?? 0), image: it.image || '' }))
      .filter(it => it.id && it.label && !it.hidden);
    if (!items.length) { ouvrirWizard(produit); return; }
    setSubView({ produit, items, groupId: firstGroupId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Commande : le lieu de retrait est demandé à chaque produit ──
  // Groupes "type" : on garde l'écran intermédiaire de Biscornue pour eux.
  const TYPE_GROUPS = ['type_sand', 'type_tacos', 'type_burger', 'choix_enfant', 'choix_pf'];

  const continueToWizard = (pw) => {
    if (!pw) return;
    if (pw.groupId) {
      setWizardInitialPicks({ [pw.groupId]: [pw.item] });
      setWizardProduit(pw.produit);
    } else if (pw.sub) {
      openSubView(pw.produit);
    } else {
      setWizardProduit(pw.produit);
    }
  };

  const openWizardFromSub = (produit, groupId, item) => {
    setPendingWizard({ produit, groupId, item });
    setShowPointPicker(true);
  };

  const startCommande = (produit) => {
    const first = produit.optionGroupIds?.[0];
    const useSub = !!first && TYPE_GROUPS.includes(first);
    setPendingWizard({ produit, sub: useSub });
    setShowPointPicker(true);
  };

  const revenirAuPanierSiBesoin = () => {
    if (!returnToCart) return false;
    setReturnToCart(false);
    setShowOrder(true);
    return true;
  };

  const fermerPointPicker = () => {
    setShowPointPicker(false);
    setPendingWizard(null);
    revenirAuPanierSiBesoin();
  };

  const fermerTimePicker = () => {
    setShowTimePicker(false);
    setPendingWizard(null);
    revenirAuPanierSiBesoin();
  };

  const choisirPoint = (pt) => {
    const change = pt !== point;
    setPoint(pt);
    setShowPointPicker(false);
    // Lieu différent (ou heure pas encore choisie) → les créneaux changent, on redemande l'heure
    if (change || !heureConfirmed) {
      setHeure('');
      setHeureConfirmed(false);
      const auj = computeSlots(settings, pt, false);
      setTimePickerPage(auj.length === 0 && computeSlots(settings, pt, true).length > 0 ? -1 : 0);
      setShowTimePicker(true);
      return;
    }
    const pw = pendingWizard;
    setPendingWizard(null);
    if (revenirAuPanierSiBesoin()) return;
    continueToWizard(pw);
  };

  const confirmTimeThenWizard = () => {
    if (!heure) return;
    setHeureConfirmed(true);
    setShowTimePicker(false);
    const pw = pendingWizard;
    setPendingWizard(null);
    if (revenirAuPanierSiBesoin()) return;
    continueToWizard(pw);
  };

  const changerPointDepuisPanier = () => {
    setShowOrder(false);
    setPendingWizard(null);
    setReturnToCart(true);
    setShowPointPicker(true);
  };

  const ajouterAuPanier = (item) => {
    // If previous order is still shown, reset it so the new cart is visible
    if (orderDone) {
      setOrderDone(false);
      setOrderId(null);
      setLiveOrder(null);
    }
    if (editIndex !== null) {
      // Modification d'un article existant
      setPanier(prev => prev.map((it, i) => i === editIndex ? item : it));
      setEditIndex(null);
    } else {
      setPanier(prev => [...prev, item]);
    }
    setWizardProduit(null);
    setWizardInitialPicks({});
    setWizardInitialNote('');
    setWizardInitialQty(1);
    setWizardEditMode(false);
  };

  const [confirmDelete, setConfirmDelete] = useState(null); // index à confirmer

  const retirerDuPanier = (index) => setPanier(prev => prev.filter((_, i) => i !== index));

  const changerQuantite = (index, delta) => {
    const item = panier[index];
    const unitPrice = (item.prixTotal || 0) / (item.quantite || 1);
    const newQty = (item.quantite || 1) + delta;
    if (newQty <= 0) { setConfirmDelete(index); return; }
    setPanier(prev => prev.map((it, i) => i === index
      ? { ...it, quantite: newQty, prixTotal: unitPrice * newQty }
      : it
    ));
  };

  const total = panier.reduce((s, item) => s + (item.prixTotal || 0), 0);

  const passerCommande = async () => {
    if (settings.ferme) return;
    if (!nom.trim() || !telephone.trim() || !heure || !point) return;
    setSending(true);
    try {
      const ref = await addDoc(collection(db, 'siparisler'), {
        nom: nom.trim(), telephone: telephone.trim(),
        ...(email.trim() && { email: email.trim() }),
        heure,
        point, pointNom: POINTS[point].nom, source: 'web',
        jour: commandePourDemain ? demainLabel : 'Aujourd\'hui',
        items: panier, total,
        statut: 'nouveau', createdAt: serverTimestamp(),
      });
      setOrderId(ref.id);
      setLiveOrder({ nom: nom.trim(), heure, point, statut: 'nouveau' });
      setOrderDone(true);
      setPanier([]);
      setHeureConfirmed(false);
      setNom('');
      setTelephone('');
      setEmail('');
    } catch (e) {
      alert('Erreur: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* NAV */}
      <nav className="nav">
        <div className="nav-top-row">
          <div className="nav-brand">
            Le Ligeria
            <span className="nav-brand-sub">Restaurant · cuisine du sud, pizza &amp; dolce vita</span>
          </div>
          {!settings.ferme && (
            <button className="nav-cart" onClick={() => setShowOrder(true)}>
              🛒 Panier
              {panier.length > 0 && <span className="nav-cart-count">{panier.length}</span>}
            </button>
          )}
        </div>
        <div className="nav-hours">
          {settings.ferme ? (
            <span className="nav-hours-closed">🔴 Fermé</span>
          ) : isOuvert ? (
            <span className="nav-hours-open">
              🟢 Ouvert
              {(settings.services || []).filter(s => s.actif !== false).slice(0,1).map((s, i) => (
                <span key={i} className="nav-hours-time"> · {s.ouverture} – {s.fermeture}</span>
              ))}
            </span>
          ) : (
            <span className="nav-hours-closed">
              🔴 Fermé
              {(settings.services || []).filter(s => s.actif !== false).slice(0,1).map((s, i) => (
                <span key={i} className="nav-hours-time"> · Ouvre à {s.ouverture}</span>
              ))}
            </span>
          )}
        </div>
      </nav>

      {/* MENU SECTION */}
      <section className="menu-section" id="menu">

        {/* ── FERME: message personnalisé ── */}
        {settings.ferme ? (
          <div style={{
            display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
            minHeight:'60vh',textAlign:'center',padding:'40px 24px',
          }}>
            <div style={{fontSize:64,marginBottom:20}}>🔴</div>
            <h2 style={{fontSize:22,fontWeight:900,color:'#111',marginBottom:16}}>
              Nous sommes fermés
            </h2>
            {settings.fermeMessage ? (
              <p style={{
                fontSize:16,color:'#374151',lineHeight:1.7,maxWidth:480,
                whiteSpace:'pre-wrap',
              }}>
                {settings.fermeMessage}
              </p>
            ) : (
              <p style={{fontSize:15,color:'#6B7280'}}>
                Revenez bientôt ! 🌿
              </p>
            )}
          </div>
        ) : subView ? (
          <>
            <button className="sub-back-btn" onClick={() => setSubView(null)}>
              ← Retour aux catégories
            </button>
            <div className="menu-section-header">
              <p className="menu-section-tag">Découvrez</p>
              <h1 className="menu-section-title">{subView.produit.title}</h1>
              {subView.produit.description && (
                <p className="menu-section-sub">{subView.produit.description}</p>
              )}
              <div className="menu-section-line" />
            </div>
            <div className="cat-cards-grid sub-cards-grid">
              {subView.items.map(item => {
                const icon = CATEGORY_ICONS[subView.produit.category] || '🍽';
                return (
                  <article
                    className="cat-card"
                    key={item.id}
                    onClick={() => openWizardFromSub(subView.produit, subView.groupId, item)}
                  >
                    <div className="cat-card-image-wrap" style={{ background: '#fff' }}>
                      {item.image
                        ? <img className="cat-card-img" src={imgUrl(item.image)} alt={item.label} />
                        : <div className="cat-card-placeholder"><span>{icon}</span></div>
                      }
                    </div>
                    <div className="cat-card-body">
                      <h2 className="cat-card-title">{item.label}</h2>
                      {item.price > 0 && (
                        <div className="cat-card-price">{item.price.toFixed(2)} €</div>
                      )}
                      {item.description && (
                        <p className="cat-card-desc">{item.description}</p>
                      )}
                      <button className="cat-card-btn">
                        COMMANDER →
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          /* ── CARTE DES PIZZAS (groupée par catégorie) ── */
          <>
            <div className="menu-section-header">
              <h1 className="menu-section-title">Carte des pizzas</h1>
            </div>
            <p className="lg-lede">Touchez une pizza pour commander.</p>

            {produits.length === 0 && (
              <div className="empty-state">
                <p>Aucun produit disponible pour le moment.</p>
              </div>
            )}

            {categories.map(cat => (
              <React.Fragment key={cat || 'sans-categorie'}>
                {cat && <div className="cat-head">{cat}</div>}
                <div className="cat-cards-grid">
                  {produits.filter(p => (p.category || '') === cat).map(p => {
                    const img = p.image || images[p.id] || '';
                    return (
                      <article className="cat-card" key={p.id} onClick={() => startCommande(p)}>
                        <div className="cat-card-image-wrap">
                          {img
                            ? <img className="cat-card-img" src={imgUrl(img)} alt={p.title} />
                            : <div className="cat-card-ph">{p.title}</div>
                          }
                        </div>
                        <div className="cat-card-body">
                          <div className="cat-card-top">
                            <h2 className="cat-card-title">{p.title}</h2>
                            {(p.displayPrice || p.price) && (
                              <span className="cat-card-price">
                                {p.displayPrice || money(p.price)}
                              </span>
                            )}
                          </div>
                          {p.description && <p className="cat-card-desc">{p.description}</p>}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </React.Fragment>
            ))}
          </>
        )}
      </section>

      {/* ── PIED DE PAGE ── */}
      {!settings.ferme && !subView && (
        <footer className="lg-footer">
          <div className="lg-footer-in">
            <div>
              <span className="lg-brand">Le Ligeria</span>
              <p>Restaurant · cuisine du sud, pizza &amp; dolce vita</p>
            </div>
            <div>
              <h3>Nous trouver</h3>
              <p>
                Le Ligeria<br />
                <a href="https://www.google.com/maps/search/?api=1&query=1+Le+Cul+du+Moulin+49270+Oree+d%27Anjou"
                   target="_blank" rel="noreferrer">1 Le Cul du Moulin, 49270 Orée d’Anjou</a>
              </p>
              <p>
                Tabac Oudon<br />
                <a href="https://www.google.com/maps/search/?api=1&query=57+Rue+Alphonse+Fouschard+44521+Oudon"
                   target="_blank" rel="noreferrer">57 Rue Alphonse Fouschard, 44521 Oudon</a>
              </p>
              <p><a href="tel:+33251147063">02 51 14 70 63</a></p>
            </div>
            <div>
              <h3>Horaires</h3>
              {(settings.services || []).filter(s => s.actif !== false).map((s, i) => (
                <p className="lg-hours-row" key={i}>
                  <b>Service {i + 1}</b><span>{s.ouverture} – {s.fermeture}</span>
                </p>
              ))}
              <p style={{ marginTop: 10 }}>Préparation : {settings.delai || 15} min</p>
            </div>
          </div>
          <div className="lg-footer-bot">
            © {new Date().getFullYear()} Le Ligeria — biscornue.fr · leligeria.fr
          </div>
        </footer>
      )}


      {/* POINT DE RETRAIT */}
      {showPointPicker && (() => {
        const carte = (pt) => {
          const p = POINTS[pt];
          const auj = computeSlots(settings, pt, false);
          const dem = computeSlots(settings, pt, true);
          const prochain = auj[0] ? `Dès ${auj[0].replace(':', 'h')}`
            : dem[0] ? `Demain dès ${dem[0].replace(':', 'h')}`
            : pt === 'tab' ? `Fermé · jusqu'à ${(settings.tabacDernier || '21:00').replace(':', 'h')}` : 'Indisponible';
          return (
            <button key={pt} type="button"
              className={`lg-pt ${point === pt ? 'on' : ''}`}
              disabled={!auj.length && !dem.length}
              onClick={() => choisirPoint(pt)}>
              <span className="lg-pt-top">
                <span>
                  <span className="lg-pt-tag">{p.tag}</span>
                  <strong>{p.nom}</strong>
                </span>
                <span className="lg-pt-heure">{prochain}</span>
              </span>
              <span className="lg-pt-desc">{p.desc(settings)}</span>
              <span className="lg-pt-adr">{p.adresse}</span>
            </button>
          );
        };
        return (
          <div className="modal-overlay lg-pt-ov" onClick={fermerPointPicker}>
            <div className="modal-box" onClick={e => e.stopPropagation()}
              style={{ maxWidth: 480, width: '95%', padding: '24px 20px' }}>
              <div className="modal-header">
                <h2>Où récupérez-vous&nbsp;?</h2>
                <button className="modal-close-btn" onClick={fermerPointPicker}>✕</button>
              </div>
              <p className="lg-pt-intro">
                Votre commande est préparée au Ligeria.
                {panier.length > 0 && <strong> Ce choix s’applique à toute la commande.</strong>}
              </p>
              {carte('lig')}
              {carte('tab')}
            </div>
          </div>
        );
      })()}

      {/* TIME PICKER MODAL */}
      {showTimePicker && (() => {
        const SLOTS_PER_PAGE = 12;
        const totalPages = Math.ceil(creneauxActifs.length / SLOTS_PER_PAGE);
        const pageSlots = creneauxActifs.slice(timePickerPage * SLOTS_PER_PAGE, (timePickerPage + 1) * SLOTS_PER_PAGE);

        // ── Étape 0 : affichage de la date (uniquement pour demain) ──
        if (timePickerPage === -1) {
          return (
            <div className="modal-overlay" onClick={fermerTimePicker}>
              <div className="modal-box time-picker-modal" onClick={e => e.stopPropagation()}
                style={{maxWidth:420,width:'95%',padding:'32px 24px',textAlign:'center'}}>
                <button onClick={fermerTimePicker}
                  style={{position:'absolute',top:16,right:16,background:'none',border:'none',
                    fontSize:22,cursor:'pointer',color:'#9CA3AF',lineHeight:1}}>×</button>

                <div style={{fontSize:48,marginBottom:12}}>🌙</div>
                <p style={{fontSize:15,color:'#6B7280',marginBottom:8}}>
                  Nous sommes fermés pour ce soir.
                </p>
                <p style={{fontSize:15,color:'#374151',fontWeight:600,marginBottom:20}}>
                  Votre commande sera passée pour :
                </p>

                {/* Date mise en avant */}
                <div style={{
                  background:'#FFF7ED',border:'2px solid #F97316',borderRadius:16,
                  padding:'20px 24px',marginBottom:28,
                }}>
                  <div style={{fontSize:13,color:'#9A3412',fontWeight:600,letterSpacing:1,marginBottom:6,textTransform:'uppercase'}}>
                    📅 Date de retrait
                  </div>
                  <div style={{fontSize:26,fontWeight:900,color:'#C2410C',textTransform:'capitalize'}}>
                    {demainLabel}
                  </div>
                </div>

                <button
                  className="cat-card-btn"
                  style={{width:'100%',fontSize:16,padding:'14px'}}
                  onClick={() => setTimePickerPage(0)}
                >
                  Choisir mon heure →
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className="modal-overlay" onClick={fermerTimePicker}>
            <div className="modal-box time-picker-modal" onClick={e => e.stopPropagation()}
              style={{maxWidth:420,width:'95%',padding:'24px 20px'}}>

              {/* Header */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                <span style={{fontSize:18,fontWeight:700,color:'#111'}}>🕐 Heure de retrait</span>
                <button onClick={fermerTimePicker}
                  style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#9CA3AF',lineHeight:1}}>×</button>
              </div>
              <p style={{color:'#6B7280',fontSize:13,marginBottom:10}}>
                {point && <><strong style={{color:'var(--text)'}}>{POINTS[point].nom}</strong> · </>}
                {commandePourDemain ? `Pour demain · ${demainLabel}` : "Pour aujourd'hui"}
              </p>
              {point && <p className="lg-slot-note">{POINTS[point].note(settings)}</p>}

              {/* Selected time display */}
              <div style={{
                background:'var(--bg)',border:'2px solid var(--green)',borderRadius:12,
                padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10
              }}>
                <span style={{fontSize:13,color:'#6B7280',flex:1}}>{point === 'tab' ? 'Disponible au Tabac Oudon à :' : 'Votre commande sera prête à :'}</span>
                <span style={{fontSize:28,fontWeight:900,color:'var(--green)',letterSpacing:1}}>
                  {heure || '--:--'}
                </span>
              </div>

              {/* Grid of slots */}
              {creneauxActifs.length === 0 ? (
                <div style={{textAlign:'center',padding:'20px 0',color:'#EF4444',fontSize:14}}>
                  Aucun créneau disponible pour le moment.
                </div>
              ) : (
                <>
                  <div style={{
                    display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:14
                  }}>
                    {pageSlots.map(c => (
                      <button key={c} onClick={() => setHeure(c)}
                        style={{
                          padding:'12px 4px',borderRadius:10,fontSize:15,fontWeight:600,
                          cursor:'pointer',transition:'all .15s',
                          border: heure === c ? '2px solid var(--green)' : '1.5px solid #E5E7EB',
                          background: heure === c ? 'var(--green)' : '#fff',
                          color: heure === c ? '#fff' : '#374151',
                        }}
                      >{c}</button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginBottom:14}}>
                      <button
                        onClick={() => setTimePickerPage(p => Math.max(0, p - 1))}
                        disabled={timePickerPage === 0}
                        style={{background:'none',border:'none',fontSize:20,cursor: timePickerPage===0?'default':'pointer',
                          color: timePickerPage===0?'#D1D5DB':'#374151',padding:'0 4px'}}
                      >‹</button>
                      {Array.from({length: totalPages}, (_, i) => (
                        <button key={i} onClick={() => setTimePickerPage(i)}
                          style={{
                            width:30,height:30,borderRadius:'50%',border:'none',fontSize:14,cursor:'pointer',
                            background: timePickerPage===i ? 'var(--green)' : 'none',
                            color: timePickerPage===i ? '#fff' : '#374151',
                            fontWeight: timePickerPage===i ? 700 : 400,
                            textDecoration: timePickerPage!==i ? 'underline' : 'none',
                            textDecorationColor:'#EF4444'
                          }}
                        >{i + 1}</button>
                      ))}
                      <button
                        onClick={() => setTimePickerPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={timePickerPage === totalPages - 1}
                        style={{background:'none',border:'none',fontSize:20,
                          cursor: timePickerPage===totalPages-1?'default':'pointer',
                          color: timePickerPage===totalPages-1?'#D1D5DB':'#374151',padding:'0 4px'}}
                      >›</button>
                    </div>
                  )}
                </>
              )}

              <button
                className="cat-card-btn"
                style={{width:'100%',fontSize:16,padding:'14px'}}
                onClick={confirmTimeThenWizard}
                disabled={!heure}
              >
                Confirmer →
              </button>
            </div>
          </div>
        );
      })()}

      {/* WIZARD */}
      {wizardProduit && (
        <Wizard
          produit={wizardProduit}
          optionGroups={optionGroups}
          images={images}
          onClose={() => { setWizardProduit(null); setWizardInitialPicks({}); setWizardInitialNote(''); setWizardInitialQty(1); setEditIndex(null); setWizardEditMode(false); }}
          onAjouter={ajouterAuPanier}
          initialPicks={wizardInitialPicks}
          initialNote={wizardInitialNote}
          initialQty={wizardInitialQty}
          editMode={wizardEditMode}
        />
      )}

      {/* BARRE PANIER FLOTTANTE */}
      {panier.length > 0 && !showOrder && (
        <div className="lg-bar">
          <button onClick={() => setShowOrder(true)}>
            <span>{panier.length} article{panier.length > 1 ? 's' : ''}</span>
            <span>Voir le panier · {money(total)}</span>
          </button>
        </div>
      )}

      {/* BOTTOM NAV */}
      <nav className="bottom-nav">
        <button className="bottom-nav-item active" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="bottom-nav-icon">🏠</span>
          <span>Accueil</span>
        </button>
        <button className="bottom-nav-item bottom-nav-panier" onClick={() => setShowOrder(true)}>
          <span className="bottom-nav-icon">
            🛒
            {panier.length > 0 && <span className="bottom-nav-badge">{panier.length}</span>}
          </span>
          <span>Panier</span>
        </button>
      </nav>

      {/* MODAL COMMANDE */}
      {showOrder && (
        <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setShowOrder(false)}>
          <div className="modal-box">
            {orderDone ? (
              <div className="order-live-status">
                {(!liveOrder || liveOrder.statut === 'nouveau') && (
                  <div className="order-status-waiting">
                    <div className="order-status-spinner">⏳</div>
                    <h3>Commande envoyée !</h3>
                    <p>En attente de confirmation<br />par le restaurant...</p>
                    <div className="order-status-name">Bonjour {liveOrder?.nom} 👋</div>
                  </div>
                )}
                {liveOrder?.statut === 'accepté' && (
                  <div className="order-status-accepted">
                    <div className="order-status-icon">✅</div>
                    <h3>Commande acceptée !</h3>
                    <p>{liveOrder.point === 'tab' ? 'Disponible au Tabac Oudon à' : 'Votre commande sera prête au Ligeria à'}</p>
                    <div className="order-status-time">{liveOrder.heure}</div>
                    {countdown !== null && countdown > 0 && (
                      <div className="order-status-countdown">
                        {Math.floor(countdown / 60)} min {String(countdown % 60).padStart(2, '0')} sec
                      </div>
                    )}
                    {countdown === 0 && (
                      <div className="order-status-countdown">C'est l'heure ! 🎉</div>
                    )}
                    <p className="order-status-bye">À tout à l'heure, {liveOrder.nom} ! 🎉</p>
                    <a className="google-review-btn" href="https://share.google/3oeRWEkqBqIeHdZkn" target="_blank" rel="noreferrer">
                      ⭐ Laisser un avis Google
                    </a>
                    <button className="btn-confirm" style={{ marginTop: 12 }} onClick={() => { setShowOrder(false); setOrderDone(false); setOrderId(null); setLiveOrder(null); }}>
                      🛒 Nouvelle commande
                    </button>
                  </div>
                )}
                {liveOrder?.statut === 'prêt' && (
                  <div className="order-status-accepted">
                    <div className="order-status-icon">🔔</div>
                    <h3>C'est prêt !</h3>
                    <p>Votre commande vous attend.<br />Venez la récupérer !</p>
                    <a className="google-review-btn" href="https://share.google/3oeRWEkqBqIeHdZkn" target="_blank" rel="noreferrer">
                      ⭐ Laisser un avis Google
                    </a>
                    <button className="btn-confirm" style={{ marginTop: 10 }} onClick={() => { setShowOrder(false); setOrderDone(false); setOrderId(null); setLiveOrder(null); }}>Fermer</button>
                  </div>
                )}
                {liveOrder?.statut === 'annulé' && (
                  <div className="order-status-cancelled">
                    <div className="order-status-icon">❌</div>
                    <h3>Commande annulée</h3>
                    <p>Veuillez nous appeler pour plus d'informations.</p>
                    <button className="btn-confirm" style={{ marginTop: 20 }} onClick={() => { setShowOrder(false); setOrderDone(false); setOrderId(null); setLiveOrder(null); }}>Fermer</button>
                  </div>
                )}
              </div>

            ) : panier.length === 0 ? (
              <div className="modal-closed">
                <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
                <h2>Votre panier est vide</h2>
                <p>Ajoutez des produits avant de passer commande.</p>
                <button className="btn-confirm" style={{ marginTop: 20 }} onClick={() => setShowOrder(false)}>Continuer mes achats</button>
              </div>

            ) : settings.ferme && creneaux.length === 0 && creneauxDemain.length === 0 ? (
              <div className="modal-closed">
                <h2>Restaurant fermé</h2>
                <p>Nous sommes actuellement fermés.<br />Revenez pendant nos heures d'ouverture.</p>
                <button className="btn-confirm" style={{ marginTop: 20 }} onClick={() => setShowOrder(false)}>Fermer</button>
              </div>

            ) : (
              <>
                <div className="modal-header">
                  <h2>Votre commande</h2>
                  <button className="modal-close-btn" onClick={() => setShowOrder(false)}>✕</button>
                </div>

                {commandePourDemain && (
                  <div className="order-demain-banner">
                    🌙 Nous sommes fermés ce soir — votre commande sera pour <strong>{demainLabel}</strong>
                  </div>
                )}

                {/* Articles */}
                <div className="order-items">
                  {panier.map((item, i) => (
                    <div className="order-item" key={i}>
                      {confirmDelete === i ? (
                        <div className="order-item-confirm">
                          <span>Supprimer cet article ?</span>
                          <div className="order-item-confirm-btns">
                            <button onClick={() => { retirerDuPanier(i); setConfirmDelete(null); }}>Oui, supprimer</button>
                            <button onClick={() => setConfirmDelete(null)}>Annuler</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="order-item-info">
                            <strong>{item.label}</strong>
                            <small>{item.resume}</small>
                            {item.note && <small style={{ color: '#B7791F', fontStyle: 'italic' }}>📝 {item.note}</small>}
                            <small style={{ color: 'var(--green)', fontWeight: 900 }}>{money(item.prixTotal)}</small>
                          </div>
                          <div className="order-item-actions">
                            <button
                              className="order-item-edit-btn"
                              title="Modifier"
                              onClick={() => {
                                setShowOrder(false);
                                setEditIndex(i);
                                setWizardInitialPicks(item.picks || {});
                                setWizardInitialNote(item.note || '');
                                setWizardInitialQty(item.quantite || 1);
                                setWizardEditMode(true);
                                setWizardProduit(produits.find(p => p.id === item.produitId) || null);
                              }}
                            >✏️</button>
                            <div className="order-item-qty">
                              <button onClick={() => changerQuantite(i, -1)}>−</button>
                              <span>{item.quantite || 1}</span>
                              <button onClick={() => changerQuantite(i, +1)}>+</button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="order-total">
                  <span>Total</span>
                  <strong>{money(total)}</strong>
                </div>

                {/* Lieu et heure de retrait */}
                <div className="order-time-block lg-retrait">
                  <div className="lg-retrait-top">
                    <div>
                      <div className="order-time-label">
                        Retrait {commandePourDemain && <span className="order-time-day">({demainLabel})</span>}
                      </div>
                      <div className="lg-retrait-lieu">{point ? POINTS[point].nom : 'Lieu non choisi'}</div>
                      {point && <div className="lg-retrait-adr">{POINTS[point].adresse}</div>}
                    </div>
                    <div className="lg-retrait-heure">{heure || '--:--'}</div>
                  </div>
                  <button type="button" className="lg-retrait-btn" onClick={changerPointDepuisPanier}>
                    Modifier le lieu ou l’heure
                  </button>
                </div>

                {/* Formulaire */}
                <div className="order-form">
                  <label>
                    Votre prénom *
                    <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex : Marie" />
                  </label>
                  <label>
                    Téléphone *
                    <input type="tel" value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="Ex : 06 12 34 56 78" />
                  </label>
                  <label>
                    Email <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>(optionnel — pour recevoir une copie)</span>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Ex : marie@email.com" />
                  </label>
                  <button
                    className="btn-confirm"
                    onClick={passerCommande}
                    disabled={sending || !nom.trim() || !telephone.trim() || !heure || !point || creneauxActifs.length === 0}
                  >
                    {sending ? 'Envoi en cours...' : `Confirmer — ${money(total)}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Home;
